import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSprint, useCompleteSprint } from '@/hooks/use-sprints';
import { useTasks } from '@/hooks/use-tasks';
import { CheckCircle2, PackageOpen, ArrowRight } from 'lucide-react';
import { useMemo, useState } from 'react';

interface CompleteSprintDialogProps {
  sprintId: string;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CompleteSprintDialog({
  sprintId,
  projectId,
  open,
  onOpenChange,
}: CompleteSprintDialogProps) {
  const { data: sprint } = useSprint(sprintId);
  const { data: sprintTasksData } = useTasks(projectId, { sprintId });
  const completeMutation = useCompleteSprint(projectId);

  const [moveUnfinishedTo, setMoveUnfinishedTo] = useState<'backlog' | 'next_sprint'>('backlog');

  const sprintTasks = sprintTasksData?.data ?? [];

  const { doneCount, unfinishedCount, donePoints, totalPoints } = useMemo(() => {
    let done = 0;
    let unfinished = 0;
    let dPts = 0;
    let tPts = 0;
    for (const t of sprintTasks) {
      tPts += t.storyPoints ?? 0;
      if (t.status.category === 'done') {
        done++;
        dPts += t.storyPoints ?? 0;
      } else {
        unfinished++;
      }
    }
    return { doneCount: done, unfinishedCount: unfinished, donePoints: dPts, totalPoints: tPts };
  }, [sprintTasks]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    completeMutation.mutate(
      { id: sprintId, moveUnfinishedTo },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              Complete Sprint
            </DialogTitle>
            <DialogDescription>{sprint?.name}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-3 text-center">
                <div className="text-2xl font-bold text-green-500">{doneCount}</div>
                <div className="text-xs text-muted-foreground">Completed</div>
              </div>
              <div className="rounded-lg border border-border p-3 text-center">
                <div className="text-2xl font-bold text-orange-500">{unfinishedCount}</div>
                <div className="text-xs text-muted-foreground">Unfinished</div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              Velocity: <span className="font-semibold text-foreground">{donePoints}</span>
              <span className="text-muted-foreground"> / {totalPoints} pts</span>
            </div>

            {unfinishedCount > 0 && (
              <div className="grid gap-2">
                <Label>Move {unfinishedCount} unfinished task{unfinishedCount > 1 ? 's' : ''} to:</Label>
                <Select
                  value={moveUnfinishedTo}
                  onValueChange={(v) => setMoveUnfinishedTo(v as 'backlog' | 'next_sprint')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="backlog">
                      <div className="flex items-center gap-2">
                        <PackageOpen className="h-3.5 w-3.5" />
                        Backlog
                      </div>
                    </SelectItem>
                    <SelectItem value="next_sprint">
                      <div className="flex items-center gap-2">
                        <ArrowRight className="h-3.5 w-3.5" />
                        Next Sprint
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={completeMutation.isPending}
              className="gap-1.5 cursor-pointer"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {completeMutation.isPending ? 'Completing...' : 'Complete Sprint'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
